import { useEffect, useRef } from "react";
import { Loader2, Inbox } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/shared/EmptyState";
import TransactionRow from "./TransactionRow";

// FIN-2 — TransactionTable.
//
// Variant table (md+): table com 5 colunas e header sticky.
// Variant compact (<md): cards verticais.
// Infinite scroll via IntersectionObserver no sentinel do final.

const COLUMNS = [
  { id: "desc",     label: "Descrição",  className: "text-left" },
  { id: "cat",      label: "Categoria",  className: "text-left" },
  { id: "account",  label: "Conta",      className: "text-left" },
  { id: "date",     label: "Data",       className: "text-left" },
  { id: "amount",   label: "Valor",      className: "text-right pr-2" },
];

function SkeletonRow() {
  return (
    <tr className="border-b border-subtle/30 animate-pulse">
      <td className="py-3 pr-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-surface-card-hover" />
          <div className="h-3 w-48 bg-surface-card-hover rounded" />
        </div>
      </td>
      <td className="py-3 px-3"><div className="h-5 w-20 bg-surface-card-hover rounded-md" /></td>
      <td className="py-3 px-3"><div className="h-3 w-24 bg-surface-card-hover rounded" /></td>
      <td className="py-3 px-3"><div className="h-3 w-20 bg-surface-card-hover rounded" /></td>
      <td className="py-3 pl-3 text-right"><div className="ml-auto h-4 w-20 bg-surface-card-hover rounded" /></td>
    </tr>
  );
}

function SkeletonCompact() {
  return (
    <div className="glass-card rounded-xl p-4 animate-pulse">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="h-3 w-40 bg-surface-card-hover rounded" />
        <div className="h-4 w-16 bg-surface-card-hover rounded" />
      </div>
      <div className="flex items-center gap-2">
        <div className="h-5 w-16 bg-surface-card-hover rounded-md" />
        <div className="h-3 w-24 bg-surface-card-hover rounded" />
      </div>
    </div>
  );
}

export default function TransactionTable({
  transactions,
  isLoading,
  isAppending,
  hasMore,
  totalCount,
  fetchMore,
  onTalkAbout,
  categoriesUsed,
  onPatch,
  hasActiveFilters,
  onResetFilters,
}) {
  const sentinelRef = useRef(null);

  // IntersectionObserver pra triggar fetchMore.
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || isLoading || isAppending) return;
    const node = sentinelRef.current;
    const obs = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.isIntersecting) {
        fetchMore();
      }
    }, { rootMargin: "300px 0px" });
    obs.observe(node);
    return () => { obs.disconnect(); };
  }, [hasMore, isLoading, isAppending, fetchMore]);

  // Empty state (após load)
  if (!isLoading && transactions.length === 0) {
    return (
      <Card variant="elevated" className="glass-card py-12 px-6" data-testid="extrato-empty">
        <EmptyState
          icon={Inbox}
          title={hasActiveFilters ? "Nenhuma transação no período" : "Sem transações ainda"}
          description={hasActiveFilters
            ? "Tente alargar o período ou remover filtros pra ver outras transações."
            : "Quando você registrar uma despesa ou entrada, ela aparece aqui."}
          action={hasActiveFilters ? (
            <Button variant="secondary" size="md" onClick={onResetFilters} data-testid="extrato-empty-reset">
              Limpar filtros
            </Button>
          ) : null}
        />
      </Card>
    );
  }

  return (
    <>
      {/* MD+ — Table */}
      <Card variant="elevated" className="glass-card overflow-hidden hidden md:block" data-testid="extrato-table">
        <div className="overflow-x-auto">
          <table className="w-full text-caption">
            <thead className="sticky top-0 z-10 bg-surface-overlay/95 backdrop-blur border-b border-subtle">
              <tr>
                {COLUMNS.map(c => (
                  <th
                    key={c.id}
                    className={`py-3 px-3 text-foreground-muted text-micro font-semibold uppercase ${c.className}`}
                    style={{ letterSpacing: "0.10em" }}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && transactions.length === 0 ? (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              ) : (
                transactions.map(tx => (
                  <TransactionRow
                    key={tx.id}
                    tx={tx}
                    onTalkAbout={onTalkAbout}
                    categoriesUsed={categoriesUsed}
                    onPatch={onPatch}
                    variant="table"
                  />
                ))
              )}
              {isAppending && (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Mobile <md — Cards verticais */}
      <div className="md:hidden flex flex-col gap-2.5" data-testid="extrato-table-compact">
        {isLoading && transactions.length === 0 ? (
          <>
            <SkeletonCompact />
            <SkeletonCompact />
            <SkeletonCompact />
          </>
        ) : (
          transactions.map(tx => (
            <TransactionRow
              key={tx.id}
              tx={tx}
              onTalkAbout={onTalkAbout}
              categoriesUsed={categoriesUsed}
              onPatch={onPatch}
              variant="compact"
            />
          ))
        )}
        {isAppending && (
          <>
            <SkeletonCompact />
            <SkeletonCompact />
          </>
        )}
      </div>

      {/* Sentinel + footer */}
      {hasMore && (
        <div ref={sentinelRef} className="h-1" aria-hidden="true" data-testid="extrato-sentinel" />
      )}

      {!hasMore && transactions.length > 0 && (
        <p
          className="text-caption text-foreground-muted text-center py-4"
          data-testid="extrato-end"
        >
          Você chegou ao fim · {totalCount.toLocaleString("pt-BR")} transações no período
        </p>
      )}

      {isAppending && hasMore && (
        <div className="flex items-center justify-center py-2 text-foreground-muted gap-2 text-caption">
          <Loader2 className="w-4 h-4 animate-spin" /> carregando mais…
        </div>
      )}
    </>
  );
}
